/**
 * The KPI aggregate — `computeKpis`.
 *
 * This is the single most-called derivation in the product: the Dashboard header,
 * every Analytics sub-tab, the Coach's Report, Practice and the drilldowns all read
 * their numbers from here. It is also the function Phase 3 exists to move off the
 * client, which is why it lives in `domain/` rather than `selectors/` — pure code
 * imports cleanly into a Deno Edge Function, and impure code does not.
 *
 * ⚠ PORTED SEMANTICS, NOT PORTED CODE — but the order-dependent parts are ported
 * LITERALLY, because their output depends on the exact comparison:
 *
 *   - the sort key is `` `${date} ${timeIn}` `` compared with `localeCompare`,
 *     matching the original character for character. Streaks and max drawdown both
 *     walk that order, so a "tidier" `<` comparison or an ISO-timestamp key is a
 *     behaviour change, not a refactor.
 *   - `timeIn: null` stringifies to the literal `"null"` inside the key, exactly as
 *     it did in the original. Preserved deliberately.
 *   - best/worst use strict `>` / `<` against ±Infinity seeds, so the FIRST trade
 *     at an extreme wins a tie. `>=` would return the last one.
 *
 * THE UNIT SPLIT, which is the part that is easy to get wrong:
 *
 *   Win/loss classification, win rate, streaks and profit factor are **always
 *   $-sign based**, in every unit. The sign of a result is unit-invariant and PF is
 *   a ratio, so re-deriving them per unit would change answers for no reason.
 *
 *   Magnitudes — `totalPnl`, `avgWin`, `avgLoss`, `expectancy`, `maxDrawdown`,
 *   best/worst ranking — are expressed in `unit`.
 *
 *   In `r`, stop-less trades have no realised R; in `ticks`, uncatalogued contracts
 *   have no tick size. `tradeValue` returns null for both, and those trades drop OUT
 *   of the magnitude aggregates while still counting in `count` / `wins` / `losses`.
 *   `valued` reports how many survived — a caller that shows `expectancy` without
 *   showing `valued` is quoting an average over an unstated population.
 */
import { findContract, tradeValue, type ContractSpec, type ValuedTrade } from './trade';
import type { PnlUnit } from './types';

/**
 * What `computeKpis` needs from a trade: `ValuedTrade` for the money, plus the two
 * fields the chronological walk orders by.
 */
export interface KpiTrade extends ValuedTrade {
  date: string;
  timeIn: string | null;
}

export interface Kpis<T extends KpiTrade = KpiTrade> {
  /** Trades that passed `counts` — the denominator for nothing, the population for everything. */
  count: number;
  wins: number;
  losses: number;
  /** Exactly-zero P&L. Excluded from win rate's denominator on purpose. */
  scratches: number;
  /** Percent, 0–100. Scratches are excluded: `wins / (wins + losses)`. */
  winRate: number;
  totalPnl: number;
  /** `Infinity` when there are winners and no losers. Not a bug — render it. */
  profitFactor: number;
  avgRR: number;
  avgWin: number;
  avgLoss: number;
  bestTrade: T | null;
  worstTrade: T | null;
  /**
   * The best/worst trade's own result IN `unit` — null when no trade had a value
   * in it at all.
   *
   * 🚨 WITHOUT THESE, A CALLER READS `bestTrade.netPnl`, WHICH IS ALWAYS DOLLARS.
   * The RANKING below is done on the unit value (`bestUv`), so in points the right
   * trade gets picked and then its DOLLAR figure is printed beside it — the tile
   * names the trade correctly and states its result wrongly. Both numbers were
   * already computed in that loop; only exposing them was missing.
   */
  bestValue: number | null;
  worstValue: number | null;
  bestStreak: number;
  worstStreak: number;
  expectancy: number;
  /** Peak-to-trough on the display-unit cumulative curve, seeded at 0. */
  maxDrawdown: number;
  unit: PnlUnit;
  /** points/ticks only: the set spans more than one instrument, so the total is meaningless. */
  mixed: boolean;
  /** How many trades had a non-null value in `unit` and so reached the magnitudes. */
  valued: number;
}

function emptyKpis<T extends KpiTrade>(unit: PnlUnit): Kpis<T> {
  return {
    count: 0, wins: 0, losses: 0, scratches: 0, winRate: 0, totalPnl: 0, profitFactor: 0,
    avgRR: 0, avgWin: 0, avgLoss: 0, bestTrade: null, worstTrade: null,
    bestStreak: 0, worstStreak: 0, expectancy: 0, maxDrawdown: 0, unit, mixed: false, valued: 0,
    bestValue: null, worstValue: null,
  };
}

/**
 * @param counts REQUIRED, unlike the original's optional last parameter.
 *
 * The old signature defaulted to `countsForMetrics`, and every caller but one
 * omitted it — which is how Practice came within one argument of reading zeros
 * across the board, since replay trades are deliberately rejected by that
 * predicate. Requiring it forces each call site to state which population it
 * means. `selectors/` supplies it; nothing else should.
 */
export function computeKpis<T extends KpiTrade>(
  trades: readonly T[],
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
  counts: (t: T) => boolean,
): Kpis<T> {
  const pop = trades.filter(counts);
  if (!pop.length) return emptyKpis<T>(unit);

  let wins = 0, losses = 0, scratches = 0, grossWin = 0, grossLoss = 0;
  let totalPnl = 0, uvWinSum = 0, uvWinN = 0, uvLossSum = 0, uvLossN = 0, valued = 0;
  let bestTrade: T | null = null, worstTrade: T | null = null;
  let bestUv = -Infinity, worstUv = Infinity;
  let avgRRSum = 0, avgRRCount = 0;
  const roots = new Set<string>();
  const perUnit = unit === 'points' || unit === 'ticks';

  for (const t of pop) {
    const p = t.netPnl ?? 0;
    if (p > 0) { wins++; grossWin += p; }
    else if (p < 0) { losses++; grossLoss += -p; }
    else scratches++;

    if (t.rrRealized != null) { avgRRSum += t.rrRealized; avgRRCount++; }

    const uv = tradeValue(t, unit, catalogue);
    if (uv == null) continue;
    valued++;
    totalPnl += uv;
    if (p > 0) { uvWinSum += uv; uvWinN++; } else if (p < 0) { uvLossSum += uv; uvLossN++; }
    if (uv > bestUv) { bestUv = uv; bestTrade = t; }
    if (uv < worstUv) { worstUv = uv; worstTrade = t; }
    if (perUnit) {
      const c = findContract(t.contract, catalogue);
      roots.add(c ? c.symbol : (t.contract || '?'));
    }
  }

  const winRate = wins + losses ? (wins / (wins + losses)) * 100 : 0;
  const profitFactor = grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0;
  const avgRR = avgRRCount ? avgRRSum / avgRRCount : 0;
  const avgWin = uvWinN ? uvWinSum / uvWinN : 0;
  const avgLoss = uvLossN ? uvLossSum / uvLossN : 0;
  const expectancy = valued ? totalPnl / valued : 0;

  // Chronological order. See the header: this key and this comparator are load-bearing.
  const sorted = pop
    .slice()
    .sort((a, b) => `${a.date} ${a.timeIn}`.localeCompare(`${b.date} ${b.timeIn}`));

  let bestStreak = 0, worstStreak = 0, curW = 0, curL = 0;
  for (const t of sorted) {
    const p = t.netPnl ?? 0;
    if (p > 0) { curW++; curL = 0; if (curW > bestStreak) bestStreak = curW; }
    else if (p < 0) { curL++; curW = 0; if (curL > worstStreak) worstStreak = curL; }
  }

  // Drawdown seeds `peak` at 0, so an account that never goes green still reports a
  // drawdown measured from flat rather than from its first (losing) trade.
  let peak = 0, cum = 0, maxDrawdown = 0;
  for (const t of sorted) {
    const uv = tradeValue(t, unit, catalogue);
    if (uv == null) continue;
    cum += uv;
    if (cum > peak) peak = cum;
    const dd = peak - cum;
    if (dd > maxDrawdown) maxDrawdown = dd;
  }

  return {
    count: pop.length, wins, losses, scratches, winRate,
    totalPnl, profitFactor, avgRR, avgWin, avgLoss,
    bestTrade, worstTrade, bestStreak, worstStreak, expectancy, maxDrawdown,
    unit, mixed: perUnit && roots.size > 1, valued,
    // ⚠ THE ±Infinity SEEDS MUST NOT ESCAPE. They stay untouched exactly when no
    // trade had a value in this unit, and an `Infinity` reaching a formatter
    // renders a literal "∞" into a P&L tile — the same trap `num()` in
    // ui/format.ts guards against, arriving from a different direction.
    bestValue: bestTrade ? bestUv : null,
    worstValue: worstTrade ? worstUv : null,
  };
}
